import { Box, Typography } from "@mui/material";
import { format, isValid, parseISO } from "date-fns";
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import SkeletonWrapper from "../../../../../common/components/functional/skeletonWrapper/SkeletonWrapper";
import { leadingZerosDateFormat } from "../../../../../common/helpers/formatDateTime";
import { LeaveAccumulationUnit } from "./leaveAccumulationMap";
import { LeaveAccumulationRow } from "./useLeaveAccumulation";

interface Props {
  rows: LeaveAccumulationRow[];
  unit: LeaveAccumulationUnit;
  labelKey: string;
  loading: boolean;
}

const LeaveAccumulationInfo = ({ rows, unit, labelKey, loading }: Props) => {
  const { t } = useTranslation("hourEntries");
  const unitBase = unit === "days" ? "unitDay" : "unitHour";

  if (loading) {
    return (
      <SkeletonWrapper
        sx={{ mt: "14px" }}
        loading={loading}
        width="350px"
        height="20px"
        animation="pulse"
      />
    );
  }

  if (rows.length === 0 || !labelKey) return null;

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: "2px", mt: "14px" }}
    >
      {rows.map((row, i) => {
        const parsedDate = row.startDate ? parseISO(row.startDate) : null;
        const datePrefixLabel = t("common:employment");
        const datePrefix =
          parsedDate && isValid(parsedDate)
            ? `${datePrefixLabel} ${format(parsedDate, leadingZerosDateFormat)}: `
            : "";
        const suffixKey =
          row.value === 1 ? `${unitBase}Singular` : `${unitBase}Plural`;

        return (
          <Box key={i} sx={{ display: "flex", gap: "4px" }}>
            {rows.length > 1 && datePrefix && (
              <Typography variant="body2" color="text.secondary">
                {datePrefix}
              </Typography>
            )}
            <Typography>{t(`${labelKey}Label`)}</Typography>
            <NumericFormat
              value={row.value}
              displayType="text"
              decimalSeparator=","
              decimalScale={2}
              renderText={(val) => (
                <Typography fontWeight={600}>
                  {val} {t(suffixKey)}
                </Typography>
              )}
            />
          </Box>
        );
      })}
    </Box>
  );
};

export default LeaveAccumulationInfo;
